import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { Star, MapPin, Clock, Calendar, ChevronRight } from 'lucide-react';
import ContactForm from './ContactForm';
import { AnimatePresence } from 'framer-motion';

const ProviderProfile = ({ provider: initialProvider, onClose }) => {
  const token = useSelector((state) => state.auth.token);
  const [provider, setProvider] = useState(initialProvider || {});
  const [otherServices, setOtherServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showContactForm, setShowContactForm] = useState(false);

  useEffect(() => {
    if (initialProvider?.id) {
      fetchProviderDetails(initialProvider.id);
    }
  }, [initialProvider]);

  const fetchProviderDetails = async (providerId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVICE_API_URL}/provider/${providerId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Api-Name': 'service-api',
          }
        }
      );
      const data = await response.json();
      setProvider({
        ...initialProvider,
        ...data.provider
      });
      // Assuming the API returns otherServices in the response
      setOtherServices(data.otherServices || []);
    } catch (error) {
      console.error('Error fetching provider details:', error);
      setProvider(initialProvider);
    } finally {
      setLoading(false);
    }
  };

  const renderRating = (rating) => (
    <div className="flex items-center gap-1">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`w-4 h-4 sm:w-5 sm:h-5 ${
            i < Math.floor(rating || 0) ? "text-yellow-400 fill-yellow-400" : "text-gray-300"
          }`}
        />
      ))}
      <span className="ml-2 text-sm sm:text-base text-gray-400">({rating || 0})</span>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-[#1a1f2e] p-4 sm:p-6 lg:p-8"
    >
      {/* Back Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={onClose}
        className="mb-6 px-4 py-2 text-sm text-yellow-400 bg-white/5 rounded-lg
                   hover:bg-white/10 transition-colors duration-200"
      >
        ← Back to results
      </motion.button>

      {/* Main Profile Section */}
      <div className="max-w-7xl mx-auto">
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10"
        >
          {/* Hero Section */}
          <div className="relative h-48 sm:h-64 lg:h-80">
            <img 
              src={provider.image_url}
              alt={provider.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          </div>

          {/* Profile Content */}
          <div className="relative px-4 sm:px-6 lg:px-8 pb-6 -mt-20">
            <div className="flex flex-col sm:flex-row gap-6 items-start">
              {/* Profile Image */}
              <motion.img 
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                src={provider.image_url}
                alt={provider.name}
                className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl object-cover 
                         border-4 border-[#1a1f2e] shadow-xl"
              />

              {/* Provider Info */}
              <div className="flex-1 space-y-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
                    {provider.name}
                  </h1>
                  <div className="mt-2">{renderRating(provider.overallRating)}</div>
                </div>

                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  {provider.description || 'No description available'}
                </p>

                {/* Quick Info */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                  <div className="flex items-center gap-2 text-gray-300">
                    <MapPin className="w-4 h-4 text-yellow-400" />
                    <span>{provider.location || 'Location available on request'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <Clock className="w-4 h-4 text-yellow-400" />
                    <span>{provider.availability || 'Flexible hours'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <Calendar className="w-4 h-4 text-yellow-400" />
                    <span>{provider.experience || 'Experience details available on request'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-yellow-400 font-semibold">
                    <span>£{provider.price || 'Price on request'}/hour</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 pt-4">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-6 py-2.5 bg-gradient-to-r from-yellow-400 to-green-400
                             text-gray-900 rounded-xl font-medium hover:shadow-lg
                             transition-all duration-200"
                  >
                    Book Now
                  </motion.button>
                  <motion.button
                    onClick={() => setShowContactForm(true)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-6 py-2.5 bg-white/10 text-white rounded-xl
                             hover:bg-white/20 transition-all duration-200"
                  >
                    Contact Provider
                  </motion.button>
                </div>

                {/* Contact Form Dialog */}
                <AnimatePresence>
                  {showContactForm && (
                    <ContactForm 
                      provider={provider}
                      isOpen={showContactForm}
                      onClose={() => setShowContactForm(false)}
                    />
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Other Services Section */}
        {otherServices.length > 0 && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mt-8 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6"
          >
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
              Other Services by {provider.name}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {otherServices.map((service) => (
                <motion.div
                  key={service.id}
                  whileHover={{ scale: 1.02 }}
                  className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10
                           hover:bg-white/10 transition-all duration-200 cursor-pointer"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-white font-semibold mb-2">{service.name}</h3>
                      <p className="text-gray-400 text-sm mb-3 line-clamp-2">
                        {service.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <p className="text-yellow-400 font-medium">£{service.price}/hour</p>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="text-sm px-3 py-1 rounded-lg bg-white/10 text-white
                                   hover:bg-white/20 transition-all duration-200"
                        >
                          View Details
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default ProviderProfile;
